import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, Search, Menu, X, User, LogOut } from "lucide-react";
import Logo from "./Logo";
import { useCart } from "../context/CartContext";
import { useCustomer } from "../context/CustomerContext";

const Navbar = () => {
  const { count, setOpen } = useCart();
  const { user: customer, logout } = useCustomer();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goAnchor = (id) => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 150);
    } else {
      const el = document.getElementById(id);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
      else el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const links = [
    { label: "Shop", testid: "nav-link-shop" },
    { label: "The Gilded Edit", anchor: "gilded-edit", testid: "nav-link-edit" },
    { label: "Our Story", anchor: "story", testid: "nav-link-story" },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[#0A0A0B]/80 backdrop-blur-md border-b border-[#d4af37]/20">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-[68px] flex items-center justify-between">
        <Link to="/" data-testid="nav-link-home" aria-label="Jwellary home">
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) =>
            l.anchor ? (
              <button
                key={l.label}
                data-testid={l.testid}
                onClick={() => goAnchor(l.anchor)}
                className="text-[13px] tracking-[0.14em] uppercase text-zinc-400 hover:text-[#f3e5ab] transition-colors"
              >
                {l.label}
              </button>
            ) : (
              <Link
                key={l.label}
                to="/shop"
                data-testid={l.testid}
                className="text-[13px] tracking-[0.14em] uppercase text-zinc-400 hover:text-[#f3e5ab] transition-colors"
              >
                {l.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-2">
          {customer ? (
            <div className="relative">
              <button
                data-testid="nav-account-button"
                onClick={() => setUserMenu((v) => !v)}
                className="w-9 h-9 rounded-full gold-hairline bg-[#18181c] text-[#f3e5ab] text-xs font-semibold uppercase flex items-center justify-center hover:border-[#d4af37]/60 transition-all"
                aria-label="Account menu"
              >
                {(customer.name || "A")[0]}
              </button>
              {userMenu && (
                <div className="absolute right-0 top-11 w-56 glass gold-hairline rounded-xl p-2 z-50" data-testid="nav-account-menu">
                  <p className="px-3 py-2 text-xs text-zinc-400 border-b border-[#d4af37]/10 mb-1 truncate">
                    Signed in as <span className="text-[#f3e5ab]">{customer.email}</span>
                  </p>
                  <button
                    data-testid="nav-logout-button"
                    onClick={() => {
                      logout();
                      setUserMenu(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-zinc-300 hover:text-[#f3e5ab] rounded-lg hover:bg-[#d4af37]/10 flex items-center gap-2 transition-colors"
                  >
                    <LogOut size={14} /> Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              data-testid="nav-account-button"
              onClick={() => navigate("/login", { state: { from: location.pathname } })}
              className="p-2.5 rounded-full text-zinc-400 hover:text-[#f3e5ab] hover:bg-[#d4af37]/10 transition-all"
              aria-label="Sign in"
            >
              <User size={18} strokeWidth={1.6} />
            </button>
          )}
          <button
            data-testid="nav-search-button"
            onClick={() => navigate("/shop")}
            className="p-2.5 rounded-full text-zinc-400 hover:text-[#f3e5ab] hover:bg-[#d4af37]/10 transition-all"
            aria-label="Search"
          >
            <Search size={18} strokeWidth={1.6} />
          </button>
          <button
            data-testid="nav-cart-button"
            onClick={() => setOpen(true)}
            className="relative p-2.5 rounded-full text-zinc-300 hover:text-[#f3e5ab] hover:bg-[#d4af37]/10 transition-all"
            aria-label="Open cart"
          >
            <ShoppingBag size={18} strokeWidth={1.6} />
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#d4af37] text-[#0a0a0b] text-[10px] font-bold flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
          <button
            className="md:hidden p-2.5 rounded-full text-zinc-300 hover:bg-[#d4af37]/10"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X size={20} strokeWidth={1.6} /> : <Menu size={20} strokeWidth={1.6} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav className="md:hidden border-t border-[#d4af37]/15 bg-[#0A0A0B]/95 backdrop-blur-md px-6 py-4 flex flex-col gap-1" data-testid="nav-mobile-menu">
          {links.map((l) =>
            l.anchor ? (
              <button
                key={l.label}
                data-testid={`${l.testid}-mobile`}
                onClick={() => goAnchor(l.anchor)}
                className="py-3 text-left text-sm tracking-[0.14em] uppercase text-zinc-300 hover:text-[#f3e5ab]"
              >
                {l.label}
              </button>
            ) : (
              <Link
                key={l.label}
                to="/shop"
                data-testid={`${l.testid}-mobile`}
                onClick={() => setMobileOpen(false)}
                className="py-3 text-sm tracking-[0.14em] uppercase text-zinc-300 hover:text-[#f3e5ab]"
              >
                {l.label}
              </Link>
            )
          )}
        </nav>
      )}
    </header>
  );
};

export default Navbar;
